import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';
import { useDevice, useSensorData, useMTCommand } from '@/hooks';
import { trackingService } from '@/api/services';
import { NAMTMessageType } from '@/types/enums';
import { findPendingMT } from '@/lib/sensor-commands';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { Spinner } from '@/components/common/Spinner/Spinner';
import { Button } from '@/components/common/Button/Button';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import {
  formatCoordinate,
  formatSpeed,
  formatDateTime,
  formatTimeAgo,
  feetToMeters,
  formatFeet,
} from '@/lib/formatters';
import { resolveSecurityMode } from '@/lib/enum-resolvers';
import { env } from '@/config/env';
import type { NAGPSObject } from '@/api/models';
import { ChartMapView } from './ChartMapView';

// Track line colors
const TRACK_COLORS = [
  '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
];

const DAY_PRESETS = [
  { label: 'Today', value: 1 },
  { label: '3 Days', value: 3 },
  { label: '7 Days', value: 7 },
  { label: '14 Days', value: 14 },
  { label: '30 Days', value: 30 },
  { label: '60 Days', value: 60 },
] as const;

// ── Map primitive helpers ──

function MapCircle({
  lat, lng, radiusFt, color = '#06B6D4', fillOpacity = 0.15, strokeOpacity = 0.5, strokeWeight = 1.5,
}: {
  lat: number; lng: number; radiusFt: number;
  color?: string; fillOpacity?: number; strokeOpacity?: number; strokeWeight?: number;
}) {
  const map = useMap();
  const ref = useRef<google.maps.Circle | null>(null);
  useEffect(() => {
    ref.current?.setMap(null);
    if (!map || radiusFt <= 0) { ref.current = null; return; }
    ref.current = new google.maps.Circle({
      center: { lat, lng }, radius: feetToMeters(radiusFt),
      fillColor: color, fillOpacity, strokeColor: color, strokeOpacity, strokeWeight, map,
    });
    return () => { ref.current?.setMap(null); ref.current = null; };
  }, [map, lat, lng, radiusFt, color, fillOpacity, strokeOpacity, strokeWeight]);
  return null;
}

function MapPolyline({ path, color, weight = 3, opacity = 0.85 }: {
  path: { lat: number; lng: number }[]; color: string; weight?: number; opacity?: number;
}) {
  const map = useMap();
  const ref = useRef<google.maps.Polyline | null>(null);
  useEffect(() => {
    ref.current?.setMap(null);
    if (!map || path.length < 2) { ref.current = null; return; }
    ref.current = new google.maps.Polyline({
      path, strokeColor: color, strokeWeight: weight, strokeOpacity: opacity, map,
    });
    return () => { ref.current?.setMap(null); ref.current = null; };
  }, [map, path, color, weight, opacity]);
  return null;
}

function FitToPosition({ lat, lng, zoom = 15 }: { lat: number; lng: number; zoom?: number }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (!map || fitted.current) return;
    map.setCenter({ lat, lng });
    map.setZoom(zoom);
    fitted.current = true;
  }, [map, lat, lng, zoom]);
  // Reset when position changes significantly (live tracking)
  useEffect(() => { fitted.current = false; }, [lat, lng]);
  return null;
}

function FitToBounds({ points }: { points: { lat: number; lng: number }[] }) {
  const map = useMap();
  useEffect(() => {
    if (!map || points.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    if (points.length === 1) {
      const c = bounds.getCenter();
      bounds.extend({ lat: c.lat() + 0.005, lng: c.lng() + 0.005 });
      bounds.extend({ lat: c.lat() - 0.005, lng: c.lng() - 0.005 });
    }
    map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
  }, [map, points]);
  return null;
}

// ── Clickable track point markers ──

function TrackPointMarker({ point, index }: { point: NAGPSObject; index: number }) {
  const [open, setOpen] = useState(false);
  const pos = { lat: point.latitude, lng: point.longitude };

  return (
    <>
      <AdvancedMarker position={pos} onClick={() => setOpen(true)} zIndex={100 + index}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: '#06B6D4', border: '2px solid #fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)', cursor: 'pointer',
        }} />
      </AdvancedMarker>
      {open && (
        <InfoWindow position={pos} onCloseClick={() => setOpen(false)}>
          <div className="min-w-[160px] text-xs space-y-0.5">
            <p className="font-semibold text-slate-800">Waypoint {index + 1}</p>
            <p>{formatDateTime(point.updatedDT || point.createdDT)}</p>
            <p>{formatCoordinate(point.latitude, point.longitude)}</p>
            {point.speed != null && <p>Speed: {formatSpeed(point.speed)}</p>}
            {point.heading != null && point.heading > 0 && <p>Heading: {point.heading}&deg;</p>}
            {point.totalExpectedErrorFt != null && point.totalExpectedErrorFt > 0 && (
              <p className="text-slate-500">Accuracy: &plusmn;{formatFeet(point.totalExpectedErrorFt)}</p>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
}

// ── Route summary for the route list panel ──

interface RouteSummary {
  index: number;
  points: NAGPSObject[];
  startTime: string;
  endTime: string;
  duration: string;
  distance: string;
  maxSpeed: number;
}

function buildRouteSummaries(tracks: NAGPSObject[][]): RouteSummary[] {
  return tracks.map((track, index) => {
    // Mirror MapTracksPopUpViewModel.cs: render every waypoint the backend
    // returned; skip a route only when it has 1 or fewer points. The mobile
    // app does NOT gate on locationValid for historical tracks — the field
    // can be false for sat-only fixes or coarse waypoints that still have
    // perfectly usable lat/lng.
    const pts = track;
    if (pts.length <= 1) return null;

    const startDT = pts[0].updatedDT || pts[0].createdDT || '';
    const endDT = pts[pts.length - 1].updatedDT || pts[pts.length - 1].createdDT || '';

    let durationStr = '';
    if (startDT && endDT) {
      const diffMs = new Date(endDT).getTime() - new Date(startDT).getTime();
      const mins = Math.round(diffMs / 60000);
      durationStr = mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
    }

    // Rough distance in nautical miles
    let totalNM = 0;
    for (let i = 1; i < pts.length; i++) {
      const dlat = pts[i].latitude - pts[i - 1].latitude;
      const dlon = pts[i].longitude - pts[i - 1].longitude;
      const deg = Math.sqrt(dlat * dlat + dlon * dlon);
      totalNM += deg * 60; // rough degrees to NM
    }

    const maxSpd = Math.max(...pts.map((p) => p.speed || 0));

    return {
      index,
      points: pts,
      startTime: startDT,
      endTime: endDT,
      duration: durationStr,
      distance: `${totalNM.toFixed(1)} NM`,
      maxSpeed: maxSpd,
    };
  }).filter(Boolean) as RouteSummary[];
}

// ── Status bar ──

function DeviceStatusBar({ gps, vesselName, securityMode }: {
  gps: NAGPSObject | null | undefined; vesselName: string; securityMode: string | undefined;
}) {
  if (!gps) return null;
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs dark:border-navy-700 dark:bg-navy-900">
      <span className="font-semibold text-slate-900 dark:text-white">{vesselName}</span>
      <span className="text-slate-400">|</span>
      {gps.locationValid && (
        <span className="text-slate-600 dark:text-slate-300">{formatCoordinate(gps.latitude, gps.longitude)}</span>
      )}
      {gps.speed != null && <span className="text-slate-600 dark:text-slate-300">{formatSpeed(gps.speed)}</span>}
      {gps.heading != null && gps.heading > 0 && (
        <span className="text-slate-600 dark:text-slate-300">HDG {gps.heading}&deg;</span>
      )}
      {gps.totalExpectedErrorFt != null && gps.totalExpectedErrorFt > 0 && (
        <span className="text-slate-500">&plusmn;{formatFeet(gps.totalExpectedErrorFt)}</span>
      )}
      {securityMode && (
        <>
          <span className="text-slate-400">|</span>
          <span className="text-slate-600 dark:text-slate-300">{resolveSecurityMode(securityMode)}</span>
        </>
      )}
      <span className="ml-auto text-slate-400">
        {gps.updatedDT && <>{formatTimeAgo(gps.updatedDT)}</>}
      </span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export function TrackingMapPage() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDevice, selectedDeviceInfo, deviceInfoLoading, selectDevice } = useDevice();
  const { sensorData } = useSensorData();

  // ── Route browsing state ──
  type ViewMode = 'live' | 'routes';
  const [viewMode, setViewMode] = useState<ViewMode>('live');
  const [tracks, setTracks] = useState<NAGPSObject[][]>([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [tracksError, setTracksError] = useState<string | null>(null);
  const [days, setDays] = useState(7);
  const [selectedRouteIdx, setSelectedRouteIdx] = useState<number | null>(null);
  const [currentInfoOpen, setCurrentInfoOpen] = useState(false);

  // Ensure device is selected from URL param
  useEffect(() => {
    if (deviceUUID && !selectedDevice) selectDevice(deviceUUID);
  }, [deviceUUID, selectedDevice, selectDevice]);

  // Live GPS from polling
  const currentGPS = selectedDeviceInfo?.gpsLocationData;
  const vesselName = selectedDeviceInfo?.yachtName?.yachtName || selectedDevice?.yachtName?.yachtName || 'Unknown Vessel';
  const securityMode = selectedDeviceInfo?.globalSettings?.securityOperatingMode;
  const deviceGeofences = sensorData?.geofenceList ?? [];

  // Load routes
  const loadRoutes = useCallback(async () => {
    if (!deviceUUID) return;
    setTracksLoading(true);
    setTracksError(null);
    try {
      const resp = await trackingService.getLocationTracks({ deviceUUID, maxNumDays: days, maxWaypoints: 200 });
      setTracks(resp.tracksList || []);
      setSelectedRouteIdx(null);
    } catch (err) {
      setTracksError(err instanceof Error ? err.message : 'Failed to load routes');
    } finally {
      setTracksLoading(false);
    }
  }, [deviceUUID, days]);

  // Build route summaries
  const routeSummaries = useMemo(() => buildRouteSummaries(tracks), [tracks]);

  // Currently displayed track (if a route is selected). No locationValid
  // filter — mirrors the mobile MapTracksPopUp which draws every waypoint
  // the backend returns.
  const activeTrack = selectedRouteIdx !== null ? tracks[selectedRouteIdx] : null;
  const activeTrackValid = activeTrack ?? [];
  const activeTrackPath = activeTrackValid.map((p) => ({ lat: p.latitude, lng: p.longitude }));

  // All tracks as paths (for "show all" mode) — same: no locationValid filter.
  const allTrackPaths = useMemo(() =>
    tracks.map((t) => t.map((p) => ({ lat: p.latitude, lng: p.longitude }))),
    [tracks],
  );

  // Map center
  const defaultCenter = useMemo(() => {
    if (currentGPS?.locationValid) return { lat: currentGPS.latitude, lng: currentGPS.longitude };
    return { lat: 25.7617, lng: -80.1918 };
  }, [currentGPS]);

  const [requestingLocation, setRequestingLocation] = useState(false);
  const [mapProvider, setMapProvider] = useState<'google' | 'chart'>('google');
  const { sendCommand, mtObjList } = useMTCommand(deviceUUID ?? '');
  // The REST call that enqueues the command resolves almost immediately —
  // the device may not actually receive/act on it until its next check-in.
  // Keep the button spinning until the MT command is confirmed received (or
  // the pending window times out), not just until the enqueue POST returns.
  const locationRequestPending =
    requestingLocation || !!findPendingMT(mtObjList, NAMTMessageType.SEND_LOCATION_REQUEST);

  const handleLocationRequest = async () => {
    if (!deviceUUID) return;
    setRequestingLocation(true);
    try {
      await sendCommand(NAMTMessageType.SEND_LOCATION_REQUEST, []);
    } finally {
      setRequestingLocation(false);
    }
  };

  // Switch to routes mode
  const openRoutes = () => {
    setViewMode('routes');
    loadRoutes();
  };

  // Back to live
  const backToLive = () => {
    setViewMode('live');
    setTracks([]);
    setSelectedRouteIdx(null);
    setTracksError(null);
  };

  if (deviceInfoLoading && !selectedDeviceInfo) return <PageSpinner />;

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-3">
      {/* Header + Status */}
      <DeviceStatusBar gps={currentGPS} vesselName={vesselName} securityMode={securityMode} />

      {/* Map action buttons — above the map so they don't overlap Google's controls */}
      <div className="flex gap-2">
        {viewMode === 'live' && (
          <>
            <Button variant="secondary" size="sm" onClick={openRoutes}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
              </svg>
              Routes
            </Button>
            <Button variant="secondary" size="sm" onClick={handleLocationRequest} loading={locationRequestPending}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
              On-Demand Location
            </Button>
          </>
        )}

        {/* Map type toggle */}
        <div className="ml-auto flex overflow-hidden rounded-lg border border-slate-300 dark:border-navy-600">
          <button
            onClick={() => setMapProvider('google')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${
              mapProvider === 'google'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-navy-800 dark:text-slate-300 dark:hover:bg-navy-700'
            }`}
          >
            Satellite
          </button>
          <button
            onClick={() => setMapProvider('chart')}
            className={`border-l border-slate-300 px-3 py-1.5 text-xs font-medium transition-colors dark:border-navy-600 ${
              mapProvider === 'chart'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-navy-800 dark:text-slate-300 dark:hover:bg-navy-700'
            }`}
          >
            Nautical Chart
          </button>
        </div>
      </div>

      {/* Main content: map + optional route panel */}
      <div className="flex flex-1 gap-3 overflow-hidden">

        {/* Route list panel (visible in routes mode) */}
        {viewMode === 'routes' && (
          <div className="flex w-80 flex-col rounded-xl border border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900">
            {/* Panel header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-navy-700">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Routes</h2>
              <button onClick={backToLive} className="text-xs text-teal-600 hover:text-teal-500 dark:text-teal-400">
                &larr; Live View
              </button>
            </div>

            {/* Day selector pills */}
            <div className="border-b border-slate-200 px-3 py-2 dark:border-navy-700">
              <div className="flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 dark:border-navy-600 dark:bg-navy-800">
                {DAY_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    onClick={() => { setDays(p.value); }}
                    className={`flex-1 rounded-md px-1 py-1 text-[10px] font-medium transition-colors ${
                      days === p.value
                        ? 'bg-white text-teal-700 shadow-sm dark:bg-navy-700 dark:text-teal-400'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Button variant="primary" size="sm" onClick={loadRoutes} loading={tracksLoading} className="flex-1 text-xs">
                  Load Routes
                </Button>
                {tracks.length > 0 && selectedRouteIdx !== null && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedRouteIdx(null)} className="text-xs">
                    Show All
                  </Button>
                )}
              </div>
            </div>

            {/* Route list */}
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-dark">
              {tracksLoading && (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="md" />
                </div>
              )}

              {tracksError && (
                <div className="px-4 py-3 text-xs text-red-500">{tracksError}</div>
              )}

              {!tracksLoading && routeSummaries.length === 0 && (
                <div className="px-4 py-8 text-center text-xs text-slate-400 dark:text-slate-500">
                  No routes found for this period
                </div>
              )}

              {routeSummaries.map((route) => {
                const isSelected = selectedRouteIdx === route.index;
                const color = TRACK_COLORS[route.index % TRACK_COLORS.length];
                return (
                  <button
                    key={route.index}
                    onClick={() => setSelectedRouteIdx(isSelected ? null : route.index)}
                    className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors dark:border-navy-800 ${
                      isSelected
                        ? 'bg-teal-50 dark:bg-teal-900/20'
                        : 'hover:bg-slate-50 dark:hover:bg-navy-800'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs font-semibold text-slate-800 dark:text-white">
                        Route {route.index + 1}
                      </span>
                      <span className="ml-auto text-[10px] text-slate-400">
                        {route.points.length} pts
                      </span>
                    </div>
                    <div className="mt-1 ml-5 space-y-0.5 text-[10px] text-slate-500 dark:text-slate-400">
                      <p>{formatDateTime(route.startTime)}</p>
                      <div className="flex gap-3">
                        <span>{route.duration}</span>
                        <span>{route.distance}</span>
                        <span>Max {formatSpeed(route.maxSpeed)}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Panel footer */}
            {routeSummaries.length > 0 && (
              <div className="border-t border-slate-200 px-4 py-2 text-[10px] text-slate-400 dark:border-navy-700">
                {routeSummaries.length} route{routeSummaries.length !== 1 ? 's' : ''} &middot;{' '}
                {tracks.reduce((s, t) => s + t.length, 0)} total points
              </div>
            )}
          </div>
        )}

        {/* Map */}
        <div className="relative flex-1 overflow-hidden rounded-xl border border-slate-200 dark:border-navy-700">

          {!currentGPS?.locationValid && viewMode === 'live' ? (
            <div className="flex h-full items-center justify-center bg-slate-100 dark:bg-navy-900">
              <EmptyState title="No location data" description="This device has not reported a GPS position yet." />
            </div>
          ) : mapProvider === 'chart' ? (
            <ChartMapView
              viewMode={viewMode}
              currentGPS={currentGPS}
              vesselName={vesselName}
              deviceGeofences={deviceGeofences}
              selectedRouteIdx={selectedRouteIdx}
              activeTrackPath={activeTrackPath}
              activeTrackValid={activeTrackValid}
              allTrackPaths={allTrackPaths}
              defaultCenter={defaultCenter}
            />
          ) : (
            <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
              <Map
                mapId="tracking-map"
                defaultCenter={defaultCenter}
                defaultZoom={14}
                style={{ height: '100%', width: '100%' }}
                gestureHandling="greedy"
                disableDefaultUI={false}
                mapTypeId="hybrid"
              >
                {/* === LIVE MODE: center on vessel, show accuracy + geofence === */}
                {viewMode === 'live' && currentGPS?.locationValid && (
                  <FitToPosition lat={currentGPS.latitude} lng={currentGPS.longitude} />
                )}

                {/* === ROUTES MODE: fit to selected route or all routes === */}
                {viewMode === 'routes' && selectedRouteIdx !== null && activeTrackPath.length > 0 && (
                  <FitToBounds points={activeTrackPath} />
                )}
                {viewMode === 'routes' && selectedRouteIdx === null && allTrackPaths.length > 0 && (
                  <FitToBounds points={allTrackPaths.flat()} />
                )}

                {/* Device geofence circles (always shown) */}
                {deviceGeofences.map((gf, idx) => gf.enabled && (
                  <MapCircle
                    key={`gf-${idx}`}
                    lat={gf.refLat} lng={gf.refLon} radiusFt={gf.radius}
                    color={gf.alertPresent ? '#EF4444' : '#22C55E'}
                  />
                ))}

                {/* === ROUTE POLYLINES === */}
                {viewMode === 'routes' && selectedRouteIdx !== null && activeTrackPath.length > 0 && (
                  <MapPolyline
                    path={activeTrackPath}
                    color={TRACK_COLORS[selectedRouteIdx % TRACK_COLORS.length]}
                    weight={4}
                  />
                )}

                {/* Show all routes when none selected */}
                {viewMode === 'routes' && selectedRouteIdx === null && allTrackPaths.map((path, i) =>
                  path.length >= 2 && (
                    <MapPolyline key={`all-${i}`} path={path} color={TRACK_COLORS[i % TRACK_COLORS.length]} />
                  ),
                )}

                {/* === TRACK POINT MARKERS (clickable, only for selected route) === */}
                {viewMode === 'routes' && selectedRouteIdx !== null && activeTrackValid.map((pt, i) => (
                  <TrackPointMarker key={`pt-${i}`} point={pt} index={i} />
                ))}

                {/* === CURRENT POSITION (always shown) === */}
                {currentGPS?.locationValid && (
                  <>
                    <AdvancedMarker
                      position={{ lat: currentGPS.latitude, lng: currentGPS.longitude }}
                      zIndex={1000}
                      onClick={() => setCurrentInfoOpen(true)}
                    >
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        backgroundColor: '#06B6D4', border: '3px solid #fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fff' }} />
                      </div>
                    </AdvancedMarker>

                    {/* Accuracy circle */}
                    {currentGPS.totalExpectedErrorFt != null && currentGPS.totalExpectedErrorFt > 0 && (
                      <MapCircle
                        lat={currentGPS.latitude} lng={currentGPS.longitude}
                        radiusFt={currentGPS.totalExpectedErrorFt}
                        color="#06B6D4" fillOpacity={0.12} strokeOpacity={0.4}
                      />
                    )}

                    {currentInfoOpen && (
                      <InfoWindow
                        position={{ lat: currentGPS.latitude, lng: currentGPS.longitude }}
                        onCloseClick={() => setCurrentInfoOpen(false)}
                      >
                        <div className="min-w-[180px] text-xs space-y-0.5">
                          <p className="font-semibold text-slate-900">{vesselName}</p>
                          <p>{formatCoordinate(currentGPS.latitude, currentGPS.longitude)}</p>
                          {currentGPS.speed != null && <p>Speed: {formatSpeed(currentGPS.speed)}</p>}
                          {currentGPS.heading != null && currentGPS.heading > 0 && <p>Heading: {currentGPS.heading}&deg;</p>}
                          {currentGPS.totalExpectedErrorFt != null && currentGPS.totalExpectedErrorFt > 0 && (
                            <p>Accuracy: &plusmn;{formatFeet(currentGPS.totalExpectedErrorFt)}</p>
                          )}
                          {currentGPS.updatedDT && (
                            <p className="text-slate-400">{formatDateTime(currentGPS.updatedDT)} ({formatTimeAgo(currentGPS.updatedDT)})</p>
                          )}
                        </div>
                      </InfoWindow>
                    )}
                  </>
                )}
              </Map>
            </APIProvider>
          )}
        </div>
      </div>
    </div>
  );
}
